// Presentation helpers for turning raw run-record fields into readable labels.

// Extract the PR number from a GitHub pull-request URL
// (e.g. https://github.com/rewindio/lazarus-api/pull/123 -> "PR-123").
// Falls back to a plain "PR" label when the URL doesn't match the expected shape.
export function prLabel(prUrl: string | null | undefined): string {
  if (!prUrl) return 'PR';
  const m = prUrl.match(/\/pull\/(\d+)/);
  return m ? `PR-${m[1]}` : 'PR';
}

// Borg user ids are `<sanitized-identity>-<sha256>` (see cli/borg_cli/aws_session.py).
// The 64-char hex digest guarantees collision-free credential scoping but is noise
// in the UI, so strip it for display and keep the readable identity prefix.
// The `@`/`+` in the original identity are sanitized to `_`, so we can't faithfully
// reconstruct the email — showing the readable prefix is unambiguous and legible.
// Surface the full id via a `title` tooltip at the call site for disambiguation.
export function friendlyUser(userId: string | null | undefined): string {
  if (!userId) return '—';
  return userId.replace(/-[0-9a-f]{64}$/i, '');
}

// The `deps` field of a run's test_env is a map of ecosystem -> state
// (e.g. { ruby: "OK", python: "WARN" }). Render it as a compact, readable string.
export function formatDeps(deps: Record<string, string> | null | undefined): string {
  if (!deps) return '—';
  const entries = Object.entries(deps);
  if (entries.length === 0) return '—';
  // Sort by ecosystem so ordering is stable regardless of how the backend built
  // the map (the CLI sorts deps keys too — cli/borg_cli/commands/status.py).
  entries.sort(([a], [b]) => a.localeCompare(b));
  return entries.map(([eco, state]) => `${eco}: ${state}`).join(', ');
}
